import React from "react";
import RoundCheckbox from "@/components/ui/form-elements/round-checkbox";
import { ProductItemInterface } from "@/assets/interfaces/products";
import { FormatCurrencyFun } from "@/contexts/cart-context";
import { getActualPrice, getActualPriceWithTieredDiscount } from "@/assets/js/utils/utils";
import QuantityUpdate from "./quantity-update";
import { VariantItem } from "@/assets/interfaces/products";
import { CartItem } from "@/assets/interfaces/carts";
import { StateType, VariantsInternalStateItem } from "../modals/select-variant";
import { capitalizeFirstLetter, enumToHumanFriendly } from "@/assets/js/utils/functions";
import { BulkDiscountBadge } from "./bages";

interface Props {
  product: ProductItemInterface;
  formatAsCurrency: FormatCurrencyFun;
  setOption: (index: number, payload: Partial<VariantsInternalStateItem>) => void;
  internalSelected: VariantsInternalStateItem[];
  deleteOption: (index: number) => void;
  updateOptionQuantity: (index: number, quantity: number) => void;
  variantOptions: VariantItem[];
}

const SelectSingleCustomOptions: React.FC<Props> = ({
  formatAsCurrency,
  setOption,
  internalSelected,
  deleteOption,
  updateOptionQuantity,
  variantOptions,
  product,
}) => {
  const isSelected = (id: string) => {
    return !!internalSelected.find((i) => i?.id === id && i?.quantity > 0);
  };

  const toggleOption = (index: number, option: VariantItem) => {
    if (!option.available_to_purchase) return;

    const selectedOption = internalSelected.find((i) => i?.id === option.id && i?.quantity > 0);
    setOption(index, { id: option.id, values: option.values, quantity: selectedOption ? 0 : 1, error: "" });
  };

  return (
    <div className="flex flex-col items-start mt-3 border border-grey-divider rounded-15 overflow-hidden">
      {variantOptions.map((option, index) => (
        <div
          className={`flex transition-all ease-out duration-200 px-3 py-3 gap-3 w-full last:mb-0 border-b border-grey-divider last:border-b-0 cursor-pointer ${
            isSelected(option.id) ? "bg-grey-fields-200 " : "hover:bg-grey-fields-100 hover:bg-opacity-30"
          }`}
          key={index}
          onClick={() => toggleOption(index, option)}
        >
          <div
            className="pt-0.5 h-min"
            onClick={(e) => {
              e.stopPropagation();
              toggleOption(index, option);
            }}
          >
            <RoundCheckbox
              name={`variant-option`}
              id={option.id}
              checked={isSelected(option.id)}
              small
              onChange={() => toggleOption(index, option)}
              disabled={!option.available_to_purchase}
            ></RoundCheckbox>
          </div>
          <div className="flex flex-col gap-y-1 w-full">
            <div className="flex items-start justify-between w-full">
              <div className="flex flex-col gap-y-1">
                <span
                  className={`text-sm md:text-1sm inline-flex gap-2 font-display ${
                    !option.available_to_purchase
                      ? "text-black-placeholder font-bold"
                      : "text-black-secondary font-bold"
                  }`}
                >
                  {enumToHumanFriendly(Object.keys(option.values)[0])}{" "}
                  {capitalizeFirstLetter(Object.values(option.values)[0])}
                  {!option.available_to_purchase && (
                    <span className="text-accent-red-500 bg-grey-fields-100 font-bold px-2 rounded-full text-xxxs inline-block ml-1.5 uppercase">
                      Out of Stock
                    </span>
                  )}
                </span>

                <span
                  className={`text-1xs mb-1 inline-block font-regular ${
                    !option.available_to_purchase ? "text-black-placeholder" : "text-black-muted"
                  }`}
                >
                  {formatAsCurrency(
                    getActualPriceWithTieredDiscount(product, internalSelected[index]?.quantity, option) *
                      (internalSelected[index]?.quantity || 1)
                  )}
                </span>
              </div>
              <BulkDiscountBadge item={product} quantity={internalSelected[index]?.quantity} />
            </div>

            {isSelected(option.id) && (
              <div onClick={(e) => e.stopPropagation()}>
                <dd className={`no-outline flex items-center rounded-full flex-shrink-0 mt-1.5`}>
                  <QuantityUpdate
                    value={internalSelected[index].quantity ?? 0}
                    onChange={(qty: number) => updateOptionQuantity(index, qty)}
                    increment={() => updateOptionQuantity(index, (internalSelected[index].quantity ?? 0) + 1)}
                    decrement={() => updateOptionQuantity(index, (internalSelected[index].quantity ?? 0) - 1)}
                    deleteAction={() => deleteOption(index)}
                  />
                </dd>
                {internalSelected[index].error && (
                  <span className="text-xs font-medium text-accent-red-500">{internalSelected[index].error}</span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SelectSingleCustomOptions;
